/**
 * Единые селекторы задач для KPI дашборда (согласованы с finance/operations очередями).
 * Overdue authoritative source: backend status OVERDUE.
 */
import type { WorkspaceApplication, WorkspaceRoute, WorkspaceTask } from '../store/api/workspaceApi';
import { getWorkspaceTaskSemantics, isFinanceWorkspaceTask } from './workspaceTaskSemantics';

export function workspaceTaskIsOverdue(task: WorkspaceTask): boolean {
  return String(task.status || '').toUpperCase() === 'OVERDUE';
}

export function isOpenWorkspaceTask(task: WorkspaceTask): boolean {
  const s = String(task.status || '').toUpperCase();
  return s === 'OPEN' || s === 'PENDING' || s === 'IN_PROGRESS' || s === '' || s === 'OVERDUE';
}

/** Задачи в зоне видимых сущностей (те же списки, что передаются в компактные очереди). */
export function tasksInVisibleEntities(
  tasks: WorkspaceTask[],
  visibleRoutes: WorkspaceRoute[],
  visibleApplications: WorkspaceApplication[]
): WorkspaceTask[] {
  const routeIds = new Set(visibleRoutes.map((r) => r.id));
  const appIds = new Set(visibleApplications.map((a) => a.id));
  return tasks.filter((t) => {
    if (t.routeId != null && routeIds.has(t.routeId)) return true;
    if (t.applicationId != null && appIds.has(t.applicationId)) return true;
    return false;
  });
}

export function overdueFinanceTasks(tasks: WorkspaceTask[]): WorkspaceTask[] {
  return tasks.filter((t) => isFinanceWorkspaceTask(t) && workspaceTaskIsOverdue(t));
}

export function overdueOperationalTasks(tasks: WorkspaceTask[]): WorkspaceTask[] {
  return tasks.filter((t) => !isFinanceWorkspaceTask(t) && workspaceTaskIsOverdue(t));
}

export function blockingFinanceTasks(tasks: WorkspaceTask[]): WorkspaceTask[] {
  return tasks.filter((t) => {
    if (!isOpenWorkspaceTask(t)) return false;
    const sem = getWorkspaceTaskSemantics(t);
    return sem.primary === 'BLOCKING' && sem.financeRelevant;
  });
}

export function blockingOperationalTasks(tasks: WorkspaceTask[]): WorkspaceTask[] {
  return tasks.filter((t) => {
    if (!isOpenWorkspaceTask(t)) return false;
    const sem = getWorkspaceTaskSemantics(t);
    return sem.primary === 'BLOCKING' && !sem.financeRelevant;
  });
}

export function requiresClosureProjection(projection: unknown): boolean {
  const p = projection as { physicalComplete?: boolean; overallComplete?: boolean } | null | undefined;
  return Boolean(p?.physicalComplete && !p?.overallComplete);
}

/** Открытые задачи по сущностям с projection «требуется закрытие». */
export function closureRelevantOpenTasks(
  tasks: WorkspaceTask[],
  visibleRoutes: WorkspaceRoute[],
  visibleApplications: WorkspaceApplication[]
): WorkspaceTask[] {
  const routeIds = new Set(visibleRoutes.filter((r) => requiresClosureProjection(r.completionProjection)).map((r) => r.id));
  const appIds = new Set(
    visibleApplications.filter((a) => requiresClosureProjection(a.completionProjection)).map((a) => a.id)
  );
  return tasks.filter(
    (t) =>
      isOpenWorkspaceTask(t) &&
      ((t.routeId != null && routeIds.has(t.routeId)) || (t.applicationId != null && appIds.has(t.applicationId)))
  );
}

export function documentRelevantOpenTasks(tasks: WorkspaceTask[]): WorkspaceTask[] {
  return tasks.filter((t) => {
    if (!isOpenWorkspaceTask(t)) return false;
    return getWorkspaceTaskSemantics(t).documentRelevant;
  });
}

export function financeAttentionRoutesCount(
  routes: WorkspaceRoute[],
  tasks: WorkspaceTask[],
  isAttentionNeeded: (projection: unknown, overdue: boolean, taskCount: number) => boolean
): number {
  return routes.filter((r) => {
    const fin = tasks.filter((t) => t.routeId === r.id && isFinanceWorkspaceTask(t));
    if (!fin.length) return false;
    const overdue = fin.some(workspaceTaskIsOverdue);
    return isAttentionNeeded((r as { completionProjection?: unknown }).completionProjection, overdue, fin.length);
  }).length;
}

export function financeAttentionApplicationsCount(
  applications: WorkspaceApplication[],
  tasks: WorkspaceTask[],
  isAttentionNeeded: (projection: unknown, overdue: boolean, taskCount: number) => boolean
): number {
  return applications.filter((a) => {
    const fin = tasks.filter((t) => t.applicationId === a.id && isFinanceWorkspaceTask(t));
    if (!fin.length) return false;
    const overdue = fin.some(workspaceTaskIsOverdue);
    return isAttentionNeeded((a as { completionProjection?: unknown }).completionProjection, overdue, fin.length);
  }).length;
}
